import React, { useState } from "react";
import useUser from "../../hooks/useUser";
import { required } from "react-admin";
import {
  BooleanInput,
  Edit,
  SimpleForm,
  TextInput,
  TopToolbar,
  ShowButton,
  SaveButton,
  Toolbar,
  useRedirect,
} from "react-admin";
import CsvParseButton from "../../components/CsvParseButton";
import CSVReplaceButton from "../../components/CSVReplaceButton";
import EditableSpreadSheet from "../../components/EditableSpreadSheet";
import DataProvider from "../../DataProvider.js";
import validateRow from "../../validators/RowValidator.js";
import ConfirmDeleteButton from "../../components/ConfirmDeleteButton";
import ListSelect from "../../components/ListSelect";
import zoneAlerts from "../../defaults/ZoneAlerts";
import CreateNewButton from "../../components/CreateNewButton";
import { parse } from "query-string";
import { Tooltip } from "react-tippy";
import NotificationSwitches from "../../components/NotificationSwitches";
import OrgSelector from "../../components/OrgSelector";
import validateContactList from "../../validators/ContactListValidator";
import CancelButton from "../../components/CancelButton";
import ContactOptInCheckBox from "../../components/ContactOptInCheckbox";

const ContactListEdit = (props) => {
  let [notifications, setNotifications] = useState({
    enabled_all_notifications: null,
    enabled_testdrill_notifications: null,
    enabled_emergency_notifications: null,
    enabled_tas_notifications: null,
    enabled_medical_notifications: null,
    enabled_lockdown_notifications: null,
    enabled_tornado_notifications: null,
    enabled_weather_notifications: null,
    enabled_falsealert_notifications: null,
    enabled_tamper_notifications: null,
    enabled_security_notifications: null,
    enabled_camera_weapon_detected_notifications: null,
    enabled_camera_crowd_detected_notifications: null,
    enabled_camera_fire_detected_notifications: null,
    enabled_camera_fight_detected_notifications: null,
    enabled_camera_face_detected_notifications: null,
  });

  const [contacts, setContacts] = useState([]);
  const updateContacts = (contacts) => setContacts(contacts);
  const update = (tbredirect) => {
    redirectTo(tbredirect);
  };
  const alertM = `Warning, this cannot be undone. This contact list will be deleted permanently.`;

  const { organization_id } = parse(props.location.search);

  const ContactlistActions = (props) => {
    const tooltipBG = {
      background: "#1E1E1E",
      color: "white",
      fontFamily: "Arial",
      padding: "15px",
      maxWidth: "150px",
      borderRadius: "10px",
      margin: "10px",
    };
    return (
      <TopToolbar>
        <Tooltip
          arrow={true}
          position="bottom"
          trigger="mouseenter"
          delay={0}
          html={
            <div style={tooltipBG}>
              Replace existing contacts with new contacts. This will DELETE
              existing contacts.
            </div>
          }
        >
          <CSVReplaceButton
            {...props}
            changeTableData={props.changeTableData}
          />
        </Tooltip>
        <Tooltip
          arrow="true"
          position="bottom"
          trigger="mouseenter"
          html={
            <div style={tooltipBG}>
              Bulk Upload contacts from csv. Fields: [firstName, lastName,
              phone, email]
            </div>
          }
        >
          <CsvParseButton changeTableData={props.changeTableData} />
        </Tooltip>
        <ShowButton basePath={props.basePath} record={props.data} />
      </TopToolbar>
    );
  };

  const ContactlistToolbar = (props) => (
    <Toolbar {...props} pristine={false}>
      <SaveButton onSave={submitData} />
      <CancelButton history={props.history} />
      <ConfirmDeleteButton
        id="myButton"
        dataprovidersource={props.dataprovidersource}
        alertmessage={props.alertM}
        update={() => update(props.tbredirect)}
      />
    </Toolbar>
  );

  const user = useUser();
  const redirectTo = useRedirect();

  const pBar = React.createRef();
  const pBarBG = React.createRef();

  let [tableData, changeTableData] = useState([]);
  let [contactIds] = useState([]);

  const editTableData = (_editData) => {
    changeTableData(_editData);
    tableData = _editData;
  };

  function updateProgress(_progress) {
    try {
      pBar.current.style.width = (_progress / tableData.length) * 100 + "%";
      pBar.current.textContent =
        Math.round((_progress / tableData.length) * 100) + "%";
    } catch (e) {
      console.log(e);
    }
  }

  const submitData = async (values, redirect) => {
    const isValid = validateContactList({ notifications, ...values });
    if (isValid) {
      try {
        pBar.current.style.display = "inline-block";
        pBarBG.current.style.display = "inline-block";
      } catch (e) {
        console.log(e);
      }
      await uploadAllContacts(values, redirect);
    } else {
      return;
    }
  };

  const uploadAllContacts = async (values, redirect) => {
    let existingContacts;
    const org_id = values.org_id;

    if (org_id)
      existingContacts = await DataProvider.getList("contacts", {
        pagination: { page: 1, perPage: 10000000000 },
        sort: { field: "firstName", order: "ASC" },
        filter: { OrganizationId: org_id },
      });
    else existingContacts.data = [];

    const contactNames = existingContacts.data.map((existingC) => {
      return {
        id: existingC.id,
        name: existingC.lastName + existingC.firstName,
      };
    });

    const testName = (name) => {
      for (let contact of contactNames) {
        if (contact.name === name) return contact.id;
      }
    };

    for (const [i, row] of tableData.entries()) {
      updateProgress(parseInt(i) + 1);
      if (validateRow(row).valid) {
        const existingID = testName(row.lName + row.fName);
        if (existingID) {
          contactIds.push(existingID);
        } else {
          const contact = await DataProvider.create("contacts", {
            data: {
              firstName: row.fName,
              lastName: row.lName,
              email: row.email,
              phone: row.phone,
              org_id: values.org_id,
              contactlist_id: values.id,
            },
          });
          contactIds.push(contact.data.id);
        }
      }
    }

    for (let contact of contacts) {
      //this is for the existing contacts
      contactIds.push(contact.id);
    }

    const cL = await DataProvider.update("contactlists", {
      id: values.id,
      data: { ...values, contact_ids: contactIds, ...notifications },
    });

    redirectTo(
      `/contactlists/${cL.data.id}/show?organization_id=${organization_id}`,
      cL
    );
  };
  return (
    <Edit
      {...props}
      title="Edit Contact List"
      actions={
        <ContactlistActions
          changeTableData={(_editData) => editTableData(_editData)}
        />
      }
    >
      <SimpleForm
        redirect="show"
        toolbar={
          <ContactlistToolbar
            tbredirect="/contactlists"
            alertmessage={alertM}
            dataprovidersource="contactlists"
            contactids={contactIds}
            history={props.history}
          />
        }
      >
        <OrgSelector
          fullWidth
          source="org_id"
          {...props}
          locked={organization_id}
          required
        />
        <TextInput
          fullWidth
          label="Name"
          source="name"
          validate={[required()]}
        />
        <TextInput fullWidth label="Description" source="description" />
        <ListSelect
          {...props}
          headers={["First Name", "Last Name"]}
          sources={["firstName", "lastName"]}
          filters={[
            { source: "fullName", placeholder: "Search by contact name:" },
          ]}
          filterorgid={true}
          target="contacts"
          label="Contacts"
          selectedsource="contact_ids"
          updatestate={updateContacts}
        />
        <NotificationSwitches
          notifications={notifications}
          setNotifications={setNotifications}
        />
        <CreateNewButton
          createtype="contact"
          ddorgid={false}
          redirectorigin="Contactlists_edit"
          path="contacts"
        />
        <EditableSpreadSheet
          tableData={tableData}
          changeTableData={(_editData) => editTableData(_editData)}
          pbarref={pBar}
          pbarbgref={pBarBG}
        />

        <BooleanInput label="" source="optIn" />
        <ContactOptInCheckBox />
      </SimpleForm>
    </Edit>
  );
};

export default ContactListEdit;
